import { debounce } from 'lodash';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';

import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import Iconify from 'src/components/iconify';

import { useTranslate } from 'src/locales';
import {
  useAddListItemsMutation,
  useGetCandidateListsQuery,
  useGetContactListsQuery,
  useGetListsPaginatedQuery,
  useMoveListItemsMutation,
} from 'src/services/lists/lists.service';
import { List, ListEntityType } from 'src/services/lists/lists.types';

/**
 * JT1-1276 Phase 5: Shows the lists this candidate/contact belongs to, on the Details page.
 * JT1-1276 Phase 4: Add a candidate/contact to another list ("copy") or move between lists.
 *
 * Brendan's requirement: visible below the "Linked" section. Both candidates and contacts.
 */

type Props = {
  entityType: ListEntityType;
  entityId: string;
};

const PICKER_SEARCH_LIMIT = 50;

export const EntityListsCard = ({ entityType, entityId }: Props) => {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();

  // membership query — pick the right endpoint per entity type
  const candidateQuery = useGetCandidateListsQuery(
    { org_candidate_id: entityId },
    { skip: entityType !== ListEntityType.CANDIDATE }
  );
  const contactQuery = useGetContactListsQuery(
    { contact_id: entityId },
    { skip: entityType !== ListEntityType.CONTACT }
  );
  const memberships = useMemo(
    () =>
      entityType === ListEntityType.CANDIDATE
        ? candidateQuery.currentData?.results ?? []
        : contactQuery.currentData?.results ?? [],
    [entityType, candidateQuery.currentData?.results, contactQuery.currentData?.results]
  );

  // picker — used by both "Add" and "Move" actions
  const [pickerSearch, setPickerSearch] = useState<string>('');
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setPickerSearch(value);
      }, 300),
    []
  );

  const { currentData: availableLists } = useGetListsPaginatedQuery({
    entity_type: entityType,
    search_query: pickerSearch,
    page: 1,
    per_page: PICKER_SEARCH_LIMIT,
  });

  const [addListItems, { isLoading: isAdding }] = useAddListItemsMutation();
  const [moveListItems, { isLoading: isMoving }] = useMoveListItemsMutation();

  // dialog state — open with `null` (add) or a list (move from that list)
  const [pickerOpen, setPickerOpen] = useState(false);
  const [moveSource, setMoveSource] = useState<List | null>(null);
  const [pickerSelection, setPickerSelection] = useState<List | null>(null);

  const openAdd = () => {
    setMoveSource(null);
    setPickerSelection(null);
    setPickerOpen(true);
  };

  const openMove = (source: List) => {
    setMoveSource(source);
    setPickerSelection(null);
    setPickerOpen(true);
  };

  const closePicker = () => {
    setPickerOpen(false);
    setPickerSelection(null);
    setMoveSource(null);
    setPickerSearch('');
  };

  const onConfirm = async () => {
    if (!pickerSelection) return;

    try {
      if (moveSource) {
        // move from a known source list into the picked target
        await moveListItems({
          list_id: pickerSelection.id,
          data: { source_list_id: moveSource.id, entities: [entityId] },
        }).unwrap();
        enqueueSnackbar(t('lists.entity_lists_card.move_success'), { variant: 'success' });
      } else {
        // add (copy) to the picked target
        await addListItems({
          list_id: pickerSelection.id,
          data: { entities: [entityId] },
        }).unwrap();
        enqueueSnackbar(t('lists.entity_lists_card.add_success'), { variant: 'success' });
      }
      closePicker();
    } catch (e) {
      enqueueSnackbar(t('lists.entity_lists_card.action_failed'), { variant: 'error' });
    }
  };

  // exclude the lists the entity already belongs to for "Add"; for "Move" also exclude the source
  const pickerOptions = useMemo(() => {
    const memberIds = new Set(memberships.map((l) => l.id));
    return (availableLists?.results ?? []).filter((l) => {
      if (moveSource && l.id === moveSource.id) return false;
      // when adding (no moveSource), hide lists the entity is already in
      if (!moveSource && memberIds.has(l.id)) return false;
      return true;
    });
  }, [availableLists, memberships, moveSource]);

  return (
    <Card>
      <CardHeader
        title={t('lists.entity_lists_card.title')}
        action={
          <Button
            size="small"
            variant="text"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={openAdd}
          >
            {t('lists.entity_lists_card.add')}
          </Button>
        }
      />
      <CardContent>
        {memberships.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('lists.entity_lists_card.empty')}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {memberships.map((list) => (
              <Stack
                key={list.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
              >
                <Chip label={list.name} variant="outlined" sx={{ maxWidth: '100%' }} />
                {/* JT1-1276: system lists (e.g. Website Registrations) are immutable —
                    moving OUT would remove the entity from the audit log, so we hide the action. */}
                {!list.system_type && (
                  <IconButton
                    size="small"
                    onClick={() => openMove(list)}
                    title={t('lists.entity_lists_card.move_menu_item')}
                  >
                    <Iconify icon="eva:swap-fill" width={18} />
                  </IconButton>
                )}
              </Stack>
            ))}
          </Stack>
        )}
      </CardContent>

      <Dialog open={pickerOpen} onClose={closePicker} fullWidth maxWidth="xs">
        <DialogTitle>
          {moveSource
            ? t('lists.entity_lists_card.move_title', { source_name: moveSource.name })
            : t('lists.entity_lists_card.add_title')}
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            sx={{ mt: 1 }}
            options={pickerOptions}
            getOptionLabel={(opt) => opt.name}
            value={pickerSelection}
            onChange={(_e, value) => setPickerSelection(value)}
            onInputChange={(_e, value) => debouncedSearch(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('lists.entity_lists_card.picker_label')}
                placeholder={t('lists.entity_lists_card.picker_placeholder')}
              />
            )}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closePicker}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            disabled={!pickerSelection || isAdding || isMoving}
            onClick={onConfirm}
          >
            {moveSource ? t('lists.entity_lists_card.move_action') : t('lists.entity_lists_card.add_action')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

